import { useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserRole } from '@bhops/shared';
import { productionPlansApi } from '@/api/production-plans';
// 추가수정 2차(26-07-27) 정정 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
import { useAuthStore } from '@/stores/auth';
import { ProductionPlanCalendar, ProductionPlanGantt } from './ProductionPlanCalendar';
import { ProductionPlanCreateModal } from './ProductionPlanCreateModal';
// P3+ — backend `productionPlansApi.allocate/release` endpoint 를 호출하는
// 운영자 UI. ProductionPlanPage 의 각 row 에 "할당" 버튼 추가.
import { ProductionPlanAllocateModal } from './ProductionPlanAllocateModal';
// FB2 §7B (결정 E) — plan → 실제 Robot 머티리얼라이즈("완성 처리") 모달.
import { MaterializeModal } from './MaterializeModal';
// round5 #10 — 제조 계획 수정 모달 (2-tier 정책).
import { ProductionPlanEditModal } from './ProductionPlanEditModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { ProductionPlan } from '@/api/production-plans';

type ViewMode = 'table' | 'calendar' | 'gantt';

// docs/plan/team-dashboards-and-production-handoff/plan.md §8.3
// ProductionPlanPage — 영업이 미생산 로봇 ETA 조회 + 제조팀이 plan CRUD.

const STATUS_LABEL: Record<string, string> = {
  PLANNED: '계획',
  IN_PROGRESS: '진행 중',
  COMPLETED: '완성',
  PARTIALLY_COMPLETED: '부분 완성',
  DELAYED: '지연',
  CANCELLED: '취소',
};
const STATUS_COLOR: Record<string, string> = {
  PLANNED: 'var(--fg-muted)',
  IN_PROGRESS: 'var(--dday-warning)',
  COMPLETED: 'var(--robot-operating)',
  PARTIALLY_COMPLETED: 'var(--dday-warning)',
  DELAYED: 'var(--severity-urgent)',
  CANCELLED: 'var(--fg-disabled)',
};

const tableWrap: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  overflow: 'auto',
};
const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 11,
  color: 'var(--fg-muted)',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '10px 12px', fontSize: 13 };

export function ProductionPlanPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canWrite =
    user?.role === UserRole.ADMIN ||
    user?.role === UserRole.MFG_MANAGER ||
    user?.role === UserRole.MANAGER;

  const [unallocatedOnly, setUnallocatedOnly] = useState(false);
  // data-lifecycle §3 — 삭제됨(soft-delete) 계획 포함 토글(제조팀 전용).
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  // P3+ — 선택된 plan 에 대해 allocate 모달 open. null 이면 닫힘.
  const [allocatePlan, setAllocatePlan] = useState<ProductionPlan | null>(null);
  // FB2 §7B — 선택된 plan 에 대해 "완성 처리"(materialize) 모달 open.
  const [materializePlan, setMaterializePlan] = useState<ProductionPlan | null>(null);
  // round5 #10 — 선택된 plan 수정 모달. null 이면 닫힘.
  const [editPlan, setEditPlan] = useState<ProductionPlan | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductionPlan | null>(null);
  // data-lifecycle §3/D3 — 셀프 완전삭제 대상(2단계 확인).
  const [hardDeleteTarget, setHardDeleteTarget] = useState<ProductionPlan | null>(null);

  const filters = useMemo(
    () => ({
      unallocatedOnly,
      ...(includeDeleted ? { includeDeleted: true } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      pageSize: 100,
    }),
    [unallocatedOnly, includeDeleted, statusFilter],
  );

  const q = useQuery({
    queryKey: ['production-plans', filters],
    queryFn: () => productionPlansApi.list(filters),
    staleTime: 30_000,
  });

  const remove = useMutation({
    mutationFn: (id: string) => productionPlansApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production-plans'] }),
  });

  // data-lifecycle §3 — soft-delete 복구(셀프, 제조팀).
  const restore = useMutation({
    mutationFn: (id: string) => productionPlansApi.restore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production-plans'] }),
  });

  // data-lifecycle §3/D3 — 셀프 완전삭제(되돌릴 수 없음).
  const hardDelete = useMutation({
    mutationFn: (id: string) => productionPlansApi.hardDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production-plans'] }),
  });

  const items = q.data?.items ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            padding: '4px 10px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-btn)',
            background: unallocatedOnly ? 'var(--bg-accent-subtle)' : 'var(--bg-surface)',
            color: unallocatedOnly ? 'var(--fg-brand)' : 'var(--fg-default)',
            cursor: 'pointer',
            fontWeight: unallocatedOnly ? 600 : 400,
          }}
          title="영업이 예약할 수 있는 수량이 남은 계획만 보여 줍니다."
        >
          <input
            type="checkbox"
            checked={unallocatedOnly}
            onChange={(e) => setUnallocatedOnly(e.target.checked)}
            style={{ margin: 0 }}
          />
          미할당 수량만
        </label>
        {/* data-lifecycle §3 — 삭제됨 포함(제조팀 전용). 삭제된 계획 복구용. */}
        {canWrite && (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              padding: '4px 10px',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-btn)',
              background: includeDeleted ? 'var(--bg-accent-subtle)' : 'var(--bg-surface)',
              color: includeDeleted ? 'var(--fg-brand)' : 'var(--fg-default)',
              cursor: 'pointer',
              fontWeight: includeDeleted ? 600 : 400,
            }}
            title="취소한 계획도 함께 보여 줍니다. 여기서 다시 복구할 수 있습니다."
          >
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              style={{ margin: 0 }}
            />
            삭제됨 포함
          </label>
        )}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          // round5 #21 — UA 기본 흰 배경이 다크에서 떠 보임 + 보더 토큰화.
          style={{
            fontSize: 12,
            padding: '4px 8px',
            background: 'var(--bg-surface)',
            color: 'var(--fg-default)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-input)',
          }}
        >
          <option value="">전체 상태</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        {/* batch-2 §1 — View mode toggle (table / calendar / gantt) */}
        <div
          style={{
            display: 'inline-flex',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-btn)',
            overflow: 'hidden',
            fontSize: 12,
          }}
        >
          {(['table', 'calendar', 'gantt'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setViewMode(m)}
              style={{
                padding: '4px 12px',
                border: 'none',
                background: viewMode === m ? 'var(--brand)' : 'var(--bg-surface)',
                color: viewMode === m ? 'var(--fg-inverse)' : 'var(--fg-default)',
                cursor: 'pointer',
                fontWeight: viewMode === m ? 600 : 400,
              }}
            >
              {m === 'table' ? '표' : m === 'calendar' ? '캘린더' : '타임라인'}
            </button>
          ))}
        </div>
        <div className="kuun-caption" style={{ marginLeft: 'auto', color: 'var(--fg-muted)' }}>
          {q.isFetching ? '불러오는 중…' : `${q.data?.total ?? 0} 건`}
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setCreateModalOpen(true)}
            style={{
              padding: '4px 14px',
              border: '1px solid var(--brand)',
              background: 'var(--brand)',
              color: 'var(--fg-inverse)',
              borderRadius: 'var(--r-btn)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + 신규 계획
          </button>
        )}
      </div>

      <ProductionPlanCreateModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />
      {allocatePlan && (
        <ProductionPlanAllocateModal
          open={!!allocatePlan}
          onClose={() => setAllocatePlan(null)}
          plan={allocatePlan}
        />
      )}
      {materializePlan && (
        <MaterializeModal
          open={!!materializePlan}
          onClose={() => setMaterializePlan(null)}
          plan={materializePlan}
        />
      )}
      {editPlan && (
        <ProductionPlanEditModal
          key={editPlan.id}
          open={!!editPlan}
          onClose={() => setEditPlan(null)}
          plan={editPlan}
        />
      )}

      {/* View dispatch */}
      {viewMode === 'calendar' && <ProductionPlanCalendar plans={items} />}
      {viewMode === 'gantt' && <ProductionPlanGantt plans={items} />}
      {viewMode === 'table' && (
        /* Table */
        <div style={tableWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>계획 ID</th>
                <th style={th}>모델</th>
                {/* S2 Phase 2.9 (§9.4) — full 세분화 spec 3축 컬럼. */}
                <th style={th}>색상</th>
                <th style={th}>불감지</th>
                <th style={th}>구분 사양</th>
                <th style={{ ...th, textAlign: 'right' }}>계획 수량</th>
                <th style={{ ...th, textAlign: 'right' }}>가용</th>
                <th style={{ ...th, textAlign: 'right' }}>완성 예정일</th>
                <th style={{ ...th, textAlign: 'right' }}>진행률</th>
                <th style={th}>상태</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ ...td, textAlign: 'center', color: 'var(--fg-muted)' }}>
                    {q.isLoading ? '불러오는 중…' : '제조 계획이 없습니다.'}
                  </td>
                </tr>
              )}
              {items.map((p) => (
                <tr
                  key={p.id}
                  style={{
                    borderTop: '1px solid var(--border-subtle)',
                    // data-lifecycle §3 — 삭제됨 row 는 흐리게.
                    opacity: p.deletedAt ? 0.55 : 1,
                  }}
                >
                  <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                    {p.humanId}
                  </td>
                  <td style={td}>{p.model?.displayName ?? p.modelId}</td>
                  {/* S2 Phase 2.9 (§9.4) — spec 3축 (null → —). */}
                  <td style={td}>{p.color ?? '—'}</td>
                  <td style={td}>{p.sensorType ?? '—'}</td>
                  <td style={td}>{p.variantValue ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{p.plannedCount}</td>
                  <td
                    style={{
                      ...td,
                      textAlign: 'right',
                      color:
                        (p.availableCount ?? 0) > 0 ? 'var(--robot-operating)' : 'var(--fg-muted)',
                      fontWeight: (p.availableCount ?? 0) > 0 ? 600 : 400,
                    }}
                  >
                    {p.availableCount ?? '–'}
                  </td>
                  {/* 추가수정 2차(26-07-27) 정정 — 제조 대시보드와 같은 완성 예정일.
                      결정 ②A 대로 KST 날짜 표기로 통일. */}
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                    {kstYmd(p.estimatedCompletionAt) ?? '–'}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>{p.progressPercent}%</td>
                  <td style={{ ...td, color: STATUS_COLOR[p.status] ?? 'inherit' }}>
                    {p.deletedAt ? (
                      <span style={{ color: 'var(--severity-urgent)', fontWeight: 600 }}>
                        삭제됨
                      </span>
                    ) : (
                      (STATUS_LABEL[p.status] ?? p.status)
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {canWrite && p.deletedAt && (
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          type="button"
                          onClick={() => restore.mutate(p.id)}
                          disabled={restore.isPending}
                          title="취소한 계획을 되살려 계획 상태로 돌립니다."
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            border: '1px solid var(--brand)',
                            background: 'var(--bg-info-subtle)',
                            color: 'var(--fg-on-info-tint)',
                            borderRadius: 'var(--r-btn)',
                            cursor: 'pointer',
                            fontWeight: 600,
                          }}
                        >
                          복구
                        </button>
                        {/* data-lifecycle §3/D3 — 셀프 완전삭제(되돌릴 수 없음). */}
                        <button
                          type="button"
                          onClick={() => setHardDeleteTarget(p)}
                          title="이 계획을 영구 삭제합니다. 되돌릴 수 없습니다."
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            border: '1px solid var(--severity-urgent)',
                            background: 'var(--bg-surface)',
                            color: 'var(--severity-urgent)',
                            borderRadius: 'var(--r-btn)',
                            cursor: 'pointer',
                            fontWeight: 600,
                          }}
                        >
                          완전 삭제
                        </button>
                      </div>
                    )}
                    {canWrite && !p.deletedAt && (
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        {/* FB2 §7B (결정 E) — "완성 처리"(materialize). 남은
                          수량(plannedCount - materializedCount)이 있고 취소되지
                          않은 계획에만 노출. 누르면 모달에서 수량·SN 확정 후
                          로봇을 출고 대기로 생성. 남은 수량 0(=완성)·취소면 숨김. */}
                        {p.status !== 'CANCELLED' &&
                          p.plannedCount - (p.materializedCount ?? 0) > 0 && (
                            <button
                              type="button"
                              onClick={() => setMaterializePlan(p)}
                              title="제조가 끝난 로봇을 출고 대기 상태로 로봇 목록에 올립니다."
                              style={{
                                fontSize: 11,
                                padding: '2px 8px',
                                border: '1px solid var(--robot-operating)',
                                background: 'var(--bg-success-subtle)',
                                color: 'var(--fg-on-success-tint)',
                                borderRadius: 'var(--r-btn)',
                                cursor: 'pointer',
                                fontWeight: 600,
                              }}
                            >
                              완성 처리
                            </button>
                          )}
                        {/* P3+ — 할당 모달 진입점. 가용 수량 0 일 때도
                          기존 할당 release 가 필요할 수 있으므로 항상
                          노출. 모달 내부에서 신규 폼 비활성 처리. */}
                        <button
                          type="button"
                          onClick={() => setAllocatePlan(p)}
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            border: '1px solid var(--brand)',
                            background: 'var(--bg-info-subtle)',
                            color: 'var(--fg-on-info-tint)',
                            borderRadius: 'var(--r-btn)',
                            cursor: 'pointer',
                            fontWeight: 600,
                          }}
                        >
                          할당
                        </button>
                        {/* round5 #10 — 수정 진입점. 취소된 계획은 제외. */}
                        {p.status !== 'CANCELLED' && (
                          <button
                            type="button"
                            onClick={() => setEditPlan(p)}
                            title="할당이나 완성 이력이 없으면 모든 칸을, 있으면 수량과 완성 예정일, 메모를 고칠 수 있습니다."
                            style={{
                              fontSize: 11,
                              padding: '2px 8px',
                              border: '1px solid var(--border-default)',
                              background: 'var(--bg-surface)',
                              borderRadius: 'var(--r-btn)',
                              cursor: 'pointer',
                            }}
                          >
                            수정
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(p)}
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            border: '1px solid var(--border-default)',
                            background: 'var(--bg-surface)',
                            borderRadius: 'var(--r-btn)',
                            cursor: 'pointer',
                          }}
                        >
                          취소
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="kuun-caption" style={{ color: 'var(--fg-muted)', fontSize: 11 }}>
        영업팀은 "미할당 수량만"을 켜고 가용 수량을 보며 고객과 설치일을 맞춥니다. 제조팀은 위의 [+
        신규 계획]으로 모델, 색상, 불감지, 구분 사양별 계획을 등록하고, 제조가 끝나면 행의 [완성
        처리]로 로봇을 출고 대기 목록에 올립니다.
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="계획 취소"
        message={`${deleteTarget?.humanId} 계획을 취소하시겠습니까? 취소한 계획은 삭제됨으로 표시됩니다.`}
        confirmLabel="취소 확인"
        danger
        busy={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* data-lifecycle §3/D3 — 완전삭제 2단계 확인(되돌릴 수 없음). */}
      <ConfirmDialog
        open={!!hardDeleteTarget}
        title="제조 계획 완전 삭제"
        message={`${hardDeleteTarget?.humanId} 계획을 영구 삭제합니다. 되돌릴 수 없습니다. 생산된 로봇이나 활성 할당이 있으면 삭제되지 않습니다.`}
        confirmLabel="완전 삭제"
        danger
        busy={hardDelete.isPending}
        onConfirm={() => {
          if (hardDeleteTarget) hardDelete.mutate(hardDeleteTarget.id);
          setHardDeleteTarget(null);
        }}
        onCancel={() => setHardDeleteTarget(null)}
      />
    </div>
  );
}
